import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { crmMarketRegionDefaultListParams, crmMarketRegionQueryRootKey } from "../config";
import {
  createCrmMarketRegion,
  createCrmMarketRegionMember,
  createCrmMarketRegionScope,
  deleteCrmMarketRegionMember,
  deleteCrmMarketRegionScope,
  fetchCrmMarketRegionMembers,
  fetchCrmMarketRegionOrganizationOptions,
  fetchCrmMarketRegionOrganizationUnitOptions,
  fetchCrmMarketRegionScopes,
  fetchCrmMarketRegionStaffOptions,
  fetchCrmMarketRegions,
  updateCrmMarketRegion,
  updateCrmMarketRegionStatus,
} from "../repo/crmMarketRegionRepo";
import type {
  MarketRegionItem,
  MarketRegionListParams,
  MarketRegionMemberItem,
  MarketRegionMemberMutation,
  MarketRegionMutation,
  MarketRegionScopeItem,
  MarketRegionScopeMutation,
  MarketRegionScopeType,
  MarketRegionStaffOption,
} from "../types";

export const emptyMarketRegionDraft: MarketRegionMutation = {
  regionName: "",
  regionCode: "",
  regionType: "province_area",
  description: "",
  isDisabled: false,
  currentUserId: 1,
  remark: "",
};

const EMPTY_MARKET_REGION_SCOPES: MarketRegionScopeItem[] = [];
const EMPTY_MARKET_REGION_MEMBERS: MarketRegionMemberItem[] = [];
const EMPTY_MARKET_REGION_STAFF: MarketRegionStaffOption[] = [];

/** 编排区域经营配置页面的数据查询与变更行为。 */
export function useCrmMarketRegionRuntime() {
  const queryClient = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(crmMarketRegionDefaultListParams.page);
  const [pageSize, setPageSize] = useState(crmMarketRegionDefaultListParams.pageSize);
  const [selectedRegionId, setSelectedRegionId] = useState<number>();

  const listParams: MarketRegionListParams = useMemo(
    () => ({
      page,
      pageSize,
      keyword: keyword.trim() || undefined,
    }),
    [keyword, page, pageSize],
  );

  const regionsQuery = useQuery({
    queryKey: [...crmMarketRegionQueryRootKey, "list", listParams],
    queryFn: () => fetchCrmMarketRegions(listParams),
  });
  const scopesQuery = useQuery({
    queryKey: [...crmMarketRegionQueryRootKey, "scopes", selectedRegionId],
    queryFn: () => fetchCrmMarketRegionScopes(selectedRegionId ?? 0),
    enabled: Boolean(selectedRegionId),
  });
  const membersQuery = useQuery({
    queryKey: [...crmMarketRegionQueryRootKey, "members", selectedRegionId],
    queryFn: () => fetchCrmMarketRegionMembers(selectedRegionId ?? 0),
    enabled: Boolean(selectedRegionId),
  });
  const staffQuery = useQuery({
    queryKey: [...crmMarketRegionQueryRootKey, "staff-options"],
    queryFn: fetchCrmMarketRegionStaffOptions,
  });

  useEffect(() => {
    if (selectedRegionId || !regionsQuery.data?.items.length) {
      return;
    }
    setSelectedRegionId(regionsQuery.data.items[0].id);
  }, [regionsQuery.data?.items, selectedRegionId]);

  const invalidateRegions = () => queryClient.invalidateQueries({ queryKey: crmMarketRegionQueryRootKey });

  const createRegionMutation = useMutation({
    mutationFn: createCrmMarketRegion,
    onSuccess: async (region) => {
      showSuccessMessage("区域已保存");
      setSelectedRegionId(region.id);
      await invalidateRegions();
    },
  });
  const updateRegionMutation = useMutation({
    mutationFn: ({ region, payload }: { region: MarketRegionItem; payload: MarketRegionMutation }) =>
      updateCrmMarketRegion(region.id, payload),
    onSuccess: async () => {
      showSuccessMessage("区域已更新");
      await invalidateRegions();
    },
  });
  const updateStatusMutation = useMutation({
    mutationFn: ({ region, isDisabled }: { region: MarketRegionItem; isDisabled: boolean }) =>
      updateCrmMarketRegionStatus(region.id, { isDisabled, currentUserId }),
    onSuccess: async () => {
      showSuccessMessage("区域状态已更新");
      await invalidateRegions();
    },
  });
  const createScopeMutation = useMutation({
    mutationFn: (payload: MarketRegionScopeMutation) => createCrmMarketRegionScope(selectedRegionId ?? 0, payload),
    onSuccess: async () => {
      showSuccessMessage("范围已添加");
      await invalidateRegions();
    },
  });
  const deleteScopeMutation = useMutation({
    mutationFn: (scopeId: number) => deleteCrmMarketRegionScope(selectedRegionId ?? 0, scopeId),
    onSuccess: async () => {
      showSuccessMessage("范围已删除");
      await invalidateRegions();
    },
  });
  const syncProvinceScopesMutation = useMutation({
    mutationFn: async ({
      payloads,
      scopeIds,
    }: {
      payloads: MarketRegionScopeMutation[];
      scopeIds: number[];
    }) => {
      const regionId = selectedRegionId ?? 0;
      for (const payload of payloads) {
        await createCrmMarketRegionScope(regionId, payload);
      }
      for (const scopeId of scopeIds) {
        await deleteCrmMarketRegionScope(regionId, scopeId);
      }
    },
    onSuccess: async () => {
      showSuccessMessage("范围已更新");
      await invalidateRegions();
    },
  });
  const createMemberMutation = useMutation({
    mutationFn: (payload: MarketRegionMemberMutation) => createCrmMarketRegionMember(selectedRegionId ?? 0, payload),
    onSuccess: async () => {
      showSuccessMessage("成员已保存");
      await invalidateRegions();
    },
  });
  const deleteMemberMutation = useMutation({
    mutationFn: (memberId: number) => deleteCrmMarketRegionMember(selectedRegionId ?? 0, memberId),
    onSuccess: async () => {
      showSuccessMessage("成员已删除");
      await invalidateRegions();
    },
  });

  const regions = regionsQuery.data?.items ?? [];
  const selectedRegion = regions.find((item) => item.id === selectedRegionId);
  const scopes = scopesQuery.data ?? EMPTY_MARKET_REGION_SCOPES;
  const members = membersQuery.data ?? EMPTY_MARKET_REGION_MEMBERS;
  const staffOptions = staffQuery.data ?? EMPTY_MARKET_REGION_STAFF;

  return {
    currentUserId,
    setCurrentUserId,
    keyword,
    setKeyword,
    page,
    pageSize,
    total: regionsQuery.data?.total ?? 0,
    regions,
    scopes,
    members,
    staffOptions,
    selectedRegion,
    selectedRegionId,
    setSelectedRegionId,
    loading: regionsQuery.isLoading || scopesQuery.isLoading || membersQuery.isLoading,
    mutating:
      createRegionMutation.isPending ||
      updateRegionMutation.isPending ||
      updateStatusMutation.isPending ||
      createScopeMutation.isPending ||
      syncProvinceScopesMutation.isPending ||
      deleteScopeMutation.isPending ||
      createMemberMutation.isPending ||
      deleteMemberMutation.isPending,
    errorMessage: regionsQuery.error ? "区域经营配置加载失败，请稍后重试。" : "",
    handlePageChange(nextPage: number, nextPageSize?: number) {
      setPage(nextPage);
      if (nextPageSize && nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
      }
    },
    handleCreateRegion(payload: MarketRegionMutation) {
      return createRegionMutation.mutateAsync({ ...payload, currentUserId });
    },
    handleUpdateRegion(region: MarketRegionItem, payload: MarketRegionMutation) {
      return updateRegionMutation.mutateAsync({ region, payload: { ...payload, currentUserId } });
    },
    handleUpdateStatus(region: MarketRegionItem, isDisabled: boolean) {
      return updateStatusMutation.mutateAsync({ region, isDisabled });
    },
    handleCreateScope(payload: Omit<MarketRegionScopeMutation, "currentUserId">) {
      return createScopeMutation.mutateAsync({ ...payload, currentUserId });
    },
    handleSyncProvinceScopes(input: {
      payloads: Array<Omit<MarketRegionScopeMutation, "currentUserId">>;
      scopeIds: number[];
    }) {
      return syncProvinceScopesMutation.mutateAsync({
        payloads: input.payloads.map((payload) => ({ ...payload, currentUserId })),
        scopeIds: input.scopeIds,
      });
    },
    handleDeleteScope(scopeId: number) {
      return deleteScopeMutation.mutateAsync(scopeId);
    },
    handleCreateMember(payload: Omit<MarketRegionMemberMutation, "currentUserId">) {
      return createMemberMutation.mutateAsync({ ...payload, currentUserId });
    },
    handleDeleteMember(memberId: number) {
      return deleteMemberMutation.mutateAsync(memberId);
    },
  };
}

/** 查询区域范围表单所需的学校与院系候选。 */
export function useCrmMarketRegionScopeOptions({
  scopeType,
  province,
  city,
  organizationId,
  organizationKeyword,
  orgUnitKeyword,
}: {
  scopeType: MarketRegionScopeType;
  province?: string;
  city?: string;
  organizationId?: number;
  organizationKeyword?: string;
  orgUnitKeyword?: string;
}) {
  const shouldLoadOrganizations =
    (scopeType === "organization" || scopeType === "organization_unit") && Boolean(province && city);
  const shouldLoadOrgUnits = scopeType === "organization_unit" && Boolean(organizationId);
  const organizationQuery = useQuery({
    queryKey: [
      ...crmMarketRegionQueryRootKey,
      "scope-organization-options",
      province,
      city,
      organizationKeyword,
    ],
    queryFn: () =>
      fetchCrmMarketRegionOrganizationOptions({
        province,
        city,
        keyword: organizationKeyword?.trim() || undefined,
      }),
    enabled: shouldLoadOrganizations,
  });
  const orgUnitQuery = useQuery({
    queryKey: [
      ...crmMarketRegionQueryRootKey,
      "scope-org-unit-options",
      organizationId,
      orgUnitKeyword,
    ],
    queryFn: () =>
      fetchCrmMarketRegionOrganizationUnitOptions({
        orgId: organizationId ?? 0,
        keyword: orgUnitKeyword?.trim() || undefined,
      }),
    enabled: shouldLoadOrgUnits,
  });

  return {
    organizationOptions: organizationQuery.data ?? [],
    organizationLoading: organizationQuery.isLoading || organizationQuery.isFetching,
    orgUnitOptions: orgUnitQuery.data ?? [],
    orgUnitLoading: orgUnitQuery.isLoading || orgUnitQuery.isFetching,
  };
}

/** 展示成功提示；提示组件异常时不影响已经完成的保存流程。 */
function showSuccessMessage(content: string) {
  try {
    Message.success(content);
  } catch {
    // Arco Message 在部分 React 19 本地环境会因 legacy render 失败，不能影响保存主流程。
  }
}
